import { GetRoleResponseDto } from '@modusign/openapi-client-user/generated/model';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';

import { RoleFormRolePermission, RoleFormRolePermissionName } from '../types';

const CAUTIOUS_PERMISSION = /READ_ALL/gi;

type RoleFormPermissionGroupNames = keyof typeof PERMISSION_GROUP_DISPLAY_NAMES;

const PERMISSION_GROUP_DISPLAY_NAMES = {
  WORKSPACE: '워크스페이스',
  DOCUMENT: '문서',
  EXTERNAL_DOCUMENT: '외부문서',
  TEMPLATE: '템플릿',
  BULK_REQUEST: '대량전송',
  CONTRACT_LINK: '링크서명',
  APPROVAL: '내부결재',
  EDITING_DOCUMENT: '문서 편집기',
  FORM_DRAFT: '작성서식',
  SIGNATURE: '사인·도장',
} as const;

const PERMISSION_DISPLAY_NAMES: Record<RoleFormRolePermissionName, string> = {
  // MANAGE_WORKSPACE: '워크스페이스 이름 수정' // 워크스페이스 이름 수정
  MANAGE_MEMBER: '멤버 관리', // 멤버 관리 (그룹에 멤버 추가, 멤버 역할 부여, 초대 생성, 초대 삭제, 멤버 삭제)
  MANAGE_GROUP: '그룹 관리', // 그룹 관리
  MANAGE_ROLE: '역할 관리', // 역할 관리
  MANAGE_PAYMENT: '요금제·결제 관리',
  MANAGE_CUSTOM_BRANDING: '맞춤 브랜딩 설정', // 커스텀브랜딩 관리

  CREATE_DOCUMENT: '서명 요청', // 서명 요청
  ASSIGN_DOCUMENT: '공유', // 문서 공유
  EXPORT_DOCUMENT: '다운로드·전달·추출', // 문서 추출 (다운로드, 완료 문서 전달, 입력값 추출, 감추서 다운로드)
  DELETE_DOCUMENT: '삭제', // 문서 삭제
  READ_ALL_DOCUMENT: '모든 문서 열람', // 전체 문서 읽기

  CREATE_EXTERNAL_DOCUMENT: '업로드', // 외부문서 생성
  UPDATE_EXTERNAL_DOCUMENT: '수정', // 외부문서 수정
  ASSIGN_EXTERNAL_DOCUMENT: '공유', // 외부문서 공유
  DELETE_EXTERNAL_DOCUMENT: '삭제', // 외부문서 삭제
  READ_ALL_EXTERNAL_DOCUMENT: '모든 외부문서 열람', // 전체 외부문서 읽기

  CREATE_DOCUMENT_WITH_TEMPLATE: '서명 요청', // 템플릿으로 서명 요청
  CREATE_TEMPLATE: '등록', // 템플릿 생성
  UPDATE_TEMPLATE: '수정', // 템플릿 수정
  ASSIGN_TEMPLATE: '공유', // 템플릿 공유
  DELETE_TEMPLATE: '워크스페이스에서 삭제', // 템플릿 삭제
  READ_ALL_TEMPLATE: '모든 템플릿 열람', // 전체 템플릿 읽기

  MANAGE_BULK_REQUEST: '이용·관리', // 대량전송 관리
  READ_ALL_BULK_REQUEST: '모든 대량전송 내역 보기', // 전체 대량전송 읽기

  MANAGE_CONTRACT_LINK: '이용·관리', // 링크서명 관리
  READ_ALL_CONTRACT_LINK: '모든 링크서명 내역 보기', // 전체 링크서명 읽기

  MANAGE_APPROVAL: '이용·관리', // 내부결재 관리
  READ_ALL_APPROVAL: '모든 내부결재 열람', // 전체 내부결재 읽기

  MANAGE_EDITING_DOCUMENT: '이용·관리', // 편집문서 관리
  READ_ALL_EDITING_DOCUMENT: '모든 편집문서 열람', // 전체 편집문서 읽기

  MANAGE_FORM_DRAFT: '이용·관리', // 서식 관리
  READ_ALL_FORM_DRAFT: '모든 작성서식 열람', // 전체 서식 읽기

  CREATE_SIGNATURE: '만들기', // 사인/도장 생성
  UPDATE_SIGNATURE: '수정', // 사인/도장 수정
  DELETE_SIGNATURE: '삭제', // 사인/도장 삭제
  ASSIGN_SIGNATURE: '이용관리', // 사인/도장 공유
  EXPORT_SIGNATURE: '다운로드', // 사인/도장 다운로드
  READ_ALL_SIGNATURE: '모든 사인·도장 이용', // 전체 사인/도장 읽기
};

interface RoleFormPermission extends RoleFormRolePermission {
  descriptions: string[];
  enabled: boolean;
  groupName: RoleFormPermissionGroupNames;
}

export const WORKSPACE_ROLE_PERMISSIONS: Array<RoleFormPermission> = [
  {
    descriptions: [
      '멤버 초대 관리·삭제',
      '멤버의 그룹·역할 수정',
      '멤버들이 생성하는 문서가 공유될 특정 멤버·그룹을 설정',
    ],
    enabled: false,
    groupName: 'WORKSPACE',
    name: 'MANAGE_MEMBER',
  }, // 인사 관리 (그룹에 멤버 추가, 멤버 역할 부여, 초대 생성, 초대 삭제, 멤버 삭제)
  {
    descriptions: ['그룹 생성·수정·삭제'],
    enabled: false,
    groupName: 'WORKSPACE',
    name: 'MANAGE_GROUP',
  }, // 그룹 관리
  {
    descriptions: ['역할 생성·수정·삭제'],
    enabled: false,
    groupName: 'WORKSPACE',
    name: 'MANAGE_ROLE',
  }, // 역할 관리
  {
    descriptions: ['결제 내역 보기', '결제 수단 관리', '요금제 결제·중단·변경', '사용량 충전 결제'],
    enabled: false,
    groupName: 'WORKSPACE',
    name: 'MANAGE_PAYMENT',
  }, // 요금제·결제 관리
  {
    descriptions: ['이메일, 카카오톡 채널 알림 설정 변경'],
    enabled: false,
    groupName: 'WORKSPACE',
    name: 'MANAGE_CUSTOM_BRANDING',
  }, // 커스텀브랜딩 관리
  {
    descriptions: [
      '문서 업로드·문서 편집기·작성서식으로 서명 요청',
      '수정하고 다시 보내기',
      '예약 문서 관리',
    ],
    enabled: false,
    groupName: 'DOCUMENT',
    name: 'CREATE_DOCUMENT',
  }, // 서명 요청
  {
    descriptions: ['문서를 열람 가능한 멤버·그룹 설정'],
    enabled: false,
    groupName: 'DOCUMENT',
    name: 'ASSIGN_DOCUMENT',
  }, // 문서 공유
  {
    descriptions: [
      '문서 PDF로 다운로드 ',
      '감사추적 인증서 다운로드',
      '문서 목록 내려받기',
      '완료 문서 이메일로 전달',
      '문서 입력값 추출',
    ],
    enabled: false,
    groupName: 'DOCUMENT',
    name: 'EXPORT_DOCUMENT',
  }, // 문서 추출 (다운로드, 완료 문서 전달, 필드값 추출, 감추서 다운로드)
  {
    descriptions: ['문서를 워크스페이스에서 삭제'],
    enabled: false,
    groupName: 'DOCUMENT',
    name: 'DELETE_DOCUMENT',
  }, // 문서 삭제

  {
    descriptions: ['공용 워크스페이스 내 모든 요청/수신 문서 열람'],
    enabled: false,
    groupName: 'DOCUMENT',
    name: 'READ_ALL_DOCUMENT',
  }, // 전체 문서 읽기
  {
    descriptions: ['외부문서 등록'],
    enabled: false,
    groupName: 'EXTERNAL_DOCUMENT',
    name: 'CREATE_EXTERNAL_DOCUMENT',
  }, // 외부문서 생성
  {
    descriptions: ['외부문서 수정'],
    enabled: false,
    groupName: 'EXTERNAL_DOCUMENT',
    name: 'UPDATE_EXTERNAL_DOCUMENT',
  }, // 외부문서 수정
  {
    descriptions: ['계약을 열람 또는 편집 가능한 멤버·그룹 설정'],
    enabled: false,
    groupName: 'EXTERNAL_DOCUMENT',
    name: 'ASSIGN_EXTERNAL_DOCUMENT',
  }, // 외부문서 공유
  {
    descriptions: ['외부문서를 워크스페이스에서 삭제'],
    enabled: false,
    groupName: 'EXTERNAL_DOCUMENT',
    name: 'DELETE_EXTERNAL_DOCUMENT',
  }, // 외부문서 삭제
  {
    descriptions: ['모든 외부문서 열람'],
    enabled: false,
    groupName: 'EXTERNAL_DOCUMENT',
    name: 'READ_ALL_EXTERNAL_DOCUMENT',
  }, // 전체 외부문서 읽기
  {
    descriptions: ['템플릿으로 서명 요청'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'CREATE_DOCUMENT_WITH_TEMPLATE',
  }, // 템플릿으로 서명 요청
  {
    descriptions: ['새 템플릿 만들기'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'CREATE_TEMPLATE',
  }, // 템플릿 생성
  {
    descriptions: ['템플릿 수정'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'UPDATE_TEMPLATE',
  }, // 템플릿 수정
  {
    descriptions: ['템플릿을 열람 또는 편집 가능한 멤버·그룹 설정'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'ASSIGN_TEMPLATE',
  }, // 템플릿 공유
  {
    descriptions: ['템플릿을 워크스페이스에서 삭제'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'DELETE_TEMPLATE',
  }, // 템플릿 삭제
  {
    descriptions: ['모든 템플릿 열람'],
    enabled: false,
    groupName: 'TEMPLATE',
    name: 'READ_ALL_TEMPLATE',
  }, // 전체 템플릿 읽기
  {
    descriptions: [
      '템플릿으로 대량전송 생성',
      '대량전송 수정·시작',
      '준비·대기 중인 대량전송 삭제',
    ],
    enabled: false,
    groupName: 'BULK_REQUEST',
    name: 'MANAGE_BULK_REQUEST',
  }, // 대량전송 관리
  {
    descriptions: [
      '템플릿 보기',
      '전송 상태 확인',
      '라벨 및 공유 정보 확인',
      '첨부파일 확인 및 다운로드',
    ],
    enabled: false,
    groupName: 'BULK_REQUEST',
    name: 'READ_ALL_BULK_REQUEST',
  }, // 전체 대량전송 읽기
  {
    descriptions: ['템플릿으로 링크서명 생성', '링크서명 삭제·설정 변경'],
    enabled: false,
    groupName: 'CONTRACT_LINK',
    name: 'MANAGE_CONTRACT_LINK',
  }, // 링크서명 관리
  {
    descriptions: [
      '누적 서명 현황 확인 ',
      '템플릿 보기',
      '링크 상태 확인',
      '라벨 및 공유 정보 확인',
      '첨부파일 확인 및 다운로드',
      '중복 서명 여부',
    ],
    enabled: false,
    groupName: 'CONTRACT_LINK',
    name: 'READ_ALL_CONTRACT_LINK',
  }, // 전체 링크서명 읽기
  {
    descriptions: ['기안 생성·삭제', '일반·템플릿 서명 요청 시 기안 생성'],
    enabled: false,
    groupName: 'APPROVAL',
    name: 'MANAGE_APPROVAL',
  }, // 내부결재 관리
  {
    descriptions: ['모든 내부결재 열람'],
    enabled: false,
    groupName: 'APPROVAL',
    name: 'READ_ALL_APPROVAL',
  }, // 전체 내부결재 읽기
  {
    descriptions: ['편집문서 생성·수정·삭제·다운로드', '서식에 사인·도장 넣기'],
    enabled: false,
    groupName: 'EDITING_DOCUMENT',
    name: 'MANAGE_EDITING_DOCUMENT',
  }, // 편집문서 관리
  {
    descriptions: ['모든 편집문서 열람'],
    enabled: false,
    groupName: 'EDITING_DOCUMENT',
    name: 'READ_ALL_EDITING_DOCUMENT',
  }, // 전체 편집문서 읽기
  {
    descriptions: ['서식 생성·수정·삭제·다운로드'],
    enabled: false,
    groupName: 'FORM_DRAFT',
    name: 'MANAGE_FORM_DRAFT',
  }, // 서식 관리
  {
    descriptions: ['모든 서식 열람'],
    enabled: false,
    groupName: 'FORM_DRAFT',
    name: 'READ_ALL_FORM_DRAFT',
  }, // 전체 서식 읽기
  {
    descriptions: ['새 사인·도장 만들기'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'CREATE_SIGNATURE',
  }, // 사인/도장 생성
  {
    descriptions: ['사인·도장 이름 및 설명 수정'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'UPDATE_SIGNATURE',
  }, // 사인/도장 수정
  {
    descriptions: ['사인·도장 삭제'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'DELETE_SIGNATURE',
  }, // 사인/도장 삭제
  {
    descriptions: ['사인·도장 이용 가능 멤버 또는 그룹 설정'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'ASSIGN_SIGNATURE',
  }, // 사인/도장 공유
  {
    descriptions: ['사인·도장 다운로드'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'EXPORT_SIGNATURE',
  }, // 사인/도장 다운로드
  {
    descriptions: ['모든 사인·도장 이용'],
    enabled: false,
    groupName: 'SIGNATURE',
    name: 'READ_ALL_SIGNATURE',
  }, // 전체 사인/도장 읽기
];

const workspacePermissionDisplayValues = map(
  WORKSPACE_ROLE_PERMISSIONS,
  (permission) =>
    ({
      groupDisplayName: PERMISSION_GROUP_DISPLAY_NAMES[permission.groupName],
      displayName: PERMISSION_DISPLAY_NAMES[permission.name],
      caution: CAUTIOUS_PERMISSION.test(permission.name),
      ...permission,
    }) as const
);

export const getWorkspacePermissionsByCategoryEntries = (
  enabledPermissions?: RoleFormRolePermission[] | GetRoleResponseDto['permissions'],
  strategy: 'filter' | 'map' = 'map'
) => {
  const getIsEnabled = (permissionName: string) =>
    enabledPermissions?.some((p) => p.name === 'ALL') ||
    (enabledPermissions?.some((permission) => permission.name === permissionName) ?? false);

  const appliedPermissionList =
    strategy === 'filter'
      ? workspacePermissionDisplayValues.filter((value) => getIsEnabled(value.name))
      : workspacePermissionDisplayValues.map((value) => ({
          ...value,
          enabled: enabledPermissions ? getIsEnabled(value.name) : false,
        }));

  return map(
    groupBy(appliedPermissionList, (obj) => obj.groupDisplayName),
    (permissions, groupName) => [groupName, permissions] as const
  );
};
